/**
 * GuideDetailScreen — port of garageiq-web/app/(main)/guides/[id]/GuideViewer.jsx.
 *
 * Web has 3 view states (overview / steps / complete); we keep the same.
 * Major differences vs web:
 *  - YouTube embeds use react-native-webview instead of <iframe>. Tapping the
 *    thumbnail mounts the WebView (deferred so the screen doesn't ship 4 web
 *    contexts on first paint).
 *  - DiagramRenderer is replaced with a placeholder card. Diagrams live in
 *    garageiq-web/public/diagrams/ (~200MB) — bundling them all is impractical
 *    for an MVP. Long-term: lazy-fetch from a CDN keyed on diagramFolder.
 *  - StepUserContent (per-step photo/note upload) and ManualPageViewer (PDF)
 *    are deferred. Both have placeholder cards explaining where to look.
 *  - Maintenance log modal (post-completion) replaced with a simple Alert
 *    prompt. Mileage is logged to the same AsyncStorage key the web app's
 *    GarageClient consumes (`garageiq_maint_log_<make>_<model>_<year>`).
 *  - Progress is persisted under `garageiq_progress_<id>` — same key as web.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import WebView from 'react-native-webview';
import { useNavigation, useRoute } from '@react-navigation/native';

import { getGuideById, guides as allGuides } from '../lib/guideData';
import { getSuggestedGuides } from '../lib/relatedGuides';
import { getGuideTabType, TAB_TYPE_DISPLAY } from '../lib/guideTabType';
import { storage } from '../lib/storage';
import { useGarage } from '../lib/useGarage';
import {
  IconWrench, IconSteps, IconBook, IconTrophy, IconSafety, IconCheck, IconVideo, IconParts,
} from '../components/Icons';

// ─── Reusable bits ──────────────────────────────────────────────────────────

const DIFF = {
  beginner:     { wrap: 'bg-green-500/10 border-green-500/20',   text: 'text-green-400' },
  intermediate: { wrap: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-400' },
  advanced:     { wrap: 'bg-red-500/10 border-red-500/20',       text: 'text-red-400' },
};

function DifficultyBadge({ level = 'beginner' }) {
  const s = DIFF[level] || DIFF.beginner;
  return (
    <View className={`px-2.5 py-1 rounded-full border ${s.wrap}`}>
      <Text className={`text-[11px] font-bold capitalize ${s.text}`}>{level}</Text>
    </View>
  );
}

function SafetyCallout({ note }) {
  if (!note) return null;
  return (
    <View className="flex-row gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4 mt-4">
      <IconSafety size={20} />
      <View className="flex-1">
        <Text className="text-[11px] font-black text-red-400 uppercase tracking-wide mb-1">Safety Note</Text>
        <Text className="text-sm text-red-300/80 leading-5">{note}</Text>
      </View>
    </View>
  );
}

function DiagramPlaceholder({ diagram }) {
  // Web renders diagrams from public/diagrams/<folder>/<file>.png. We did NOT
  // bundle the ~200MB of static images — see RUNNING.md "Lazy diagrams" task.
  if (!diagram) return null;
  return (
    <View className="mt-4 bg-white/5 border border-dashed border-white/15 rounded-xl p-6 items-center">
      <View className="mb-2"><IconBook size={28} /></View>
      <Text className="text-white/60 text-sm font-bold">Diagram available in web app</Text>
      <Text className="text-white/30 text-xs mt-1 text-center" numberOfLines={2}>
        {typeof diagram === 'string' ? diagram : diagram.alt || 'Annotated step diagram'}
      </Text>
    </View>
  );
}

// YouTube clip player. We render a tap-to-load placeholder so the WebView
// only spins up when the user actually wants the video — important on a
// guide with 10+ step videos.
function YouTubeEmbed({ youtubeId, title, startTime, endTime, channel, height = 200 }) {
  const [show, setShow] = useState(false);
  if (!youtubeId) return null;

  const url =
    `https://www.youtube.com/embed/${youtubeId}` +
    `?autoplay=1&rel=0` +
    (startTime != null ? `&start=${startTime}` : '') +
    (endTime != null ? `&end=${endTime}` : '');

  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const clipLabel = (startTime != null && endTime != null) ? `${fmt(startTime)} – ${fmt(endTime)}` : null;

  return (
    <View className="mt-5 rounded-xl overflow-hidden border border-white/10 bg-black/30">
      <View className="px-4 py-2 bg-white/5 border-b border-white/10 flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <Text className="text-red-500 text-sm">▶</Text>
          <Text className="text-white/70 text-[11px] font-bold uppercase tracking-wide">
            {clipLabel ? 'Step Video Clip' : 'Repair Video'}
          </Text>
        </View>
        {clipLabel && <Text className="text-white/30 text-[11px]">{clipLabel}</Text>}
      </View>
      {show ? (
        <View style={{ height }}>
          <WebView
            source={{ uri: url }}
            allowsFullscreenVideo
            javaScriptEnabled
            domStorageEnabled
            mediaPlaybackRequiresUserAction={false}
            style={{ backgroundColor: '#000' }}
          />
        </View>
      ) : (
        <Pressable onPress={() => setShow(true)} className="active:opacity-80">
          <View style={{ height }} className="bg-black/60 items-center justify-center px-4">
            <View className="w-14 h-14 bg-red-600 rounded-full items-center justify-center mb-2">
              <Text className="text-white text-xl">▶</Text>
            </View>
            <Text className="text-white text-sm font-bold text-center" numberOfLines={2}>{title || 'Tap to play'}</Text>
            {channel && <Text className="text-white/50 text-xs mt-1">by {channel}</Text>}
          </View>
        </Pressable>
      )}
    </View>
  );
}

// Inline parts panel — simplified vs. web (no Pricing API). Store picker
// kept so users can scan retailer choice; the "Load My Cart" button is a
// stub that opens the retailer site via Linking in a future revision.
function PartsPanel({ parts = [] }) {
  const [store, setStore] = useState('AutoZone');
  const stores = ['AutoZone', "O'Reilly", 'NAPA', 'Amazon'];
  const required = parts.filter(p => p.category === 'required');
  const total = required.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);

  return (
    <View className="bg-white/5 border border-white/10 rounded-2xl p-5 mt-6">
      <View className="flex-row items-center gap-2 mb-1">
        <IconParts size={18} />
        <Text className="font-black text-white text-base">Parts for This Job</Text>
      </View>
      <Text className="text-sm text-white/40 mb-4">Everything you need, pre-selected for your vehicle.</Text>

      <View className="flex-row flex-wrap mb-4">
        {stores.map(s => (
          <Pressable
            key={s}
            onPress={() => setStore(s)}
            className={`mr-2 mb-2 px-3 py-1.5 rounded-full border ${
              store === s ? 'bg-sky border-sky' : 'bg-white/5 border-white/10'
            } active:opacity-80`}
          >
            <Text className={`text-[11px] font-bold ${store === s ? 'text-white' : 'text-white/50'}`}>{s}</Text>
          </Pressable>
        ))}
      </View>

      {required.length === 0 ? (
        <Text className="text-white/40 text-sm py-3">No required parts listed for this guide.</Text>
      ) : (
        <View className="gap-2 mb-4">
          {required.map(part => (
            <View key={part.id || part.name} className="flex-row items-start gap-3 bg-white/5 rounded-xl p-3">
              <View className="w-8 h-8 bg-white/10 rounded-lg border border-white/10 items-center justify-center">
                <IconParts size={14} />
              </View>
              <View className="flex-1">
                <Text className="text-sm font-bold text-white">{part.name}</Text>
                {!!part.partNumber && <Text className="text-xs text-white/30 mt-0.5">Part #{part.partNumber}</Text>}
                {!!part.notes && <Text className="text-xs text-white/40 mt-1">{part.notes}</Text>}
              </View>
              <Text className="text-sm font-black text-sky">${(part.price || 0).toFixed(2)}</Text>
            </View>
          ))}
        </View>
      )}

      <View className="flex-row justify-between items-center py-3 border-t border-b border-white/10 mb-4">
        <Text className="font-bold text-white/50 text-sm">Total (required parts)</Text>
        <Text className="font-black text-xl text-sky">${total.toFixed(2)}</Text>
      </View>
      <Pressable
        onPress={() => Alert.alert('Coming soon', `Cart load will open ${store} via deep link in v2.`)}
        className="bg-sky py-3 rounded-xl items-center active:opacity-80"
      >
        <Text className="text-white font-bold text-sm">Load My Cart at {store} →</Text>
      </Pressable>
    </View>
  );
}

function SuggestedGuides({ items, onPress }) {
  if (!items || items.length === 0) return null;
  return (
    <View className="mt-10 pt-8 border-t border-white/10">
      <Text className="text-xl font-black text-white mb-1">Suggested Guides</Text>
      <Text className="text-sm text-white/40 mb-4">Recommended next reads.</Text>
      <View className="gap-3">
        {items.map((g, idx) => {
          const featured = idx === 0 && g._suggestedReason === 'inspection';
          const tabType = getGuideTabType(g);
          const display = TAB_TYPE_DISPLAY[tabType] || TAB_TYPE_DISPLAY.repair;
          return (
            <Pressable
              key={g.id}
              onPress={() => onPress(g)}
              className={`p-5 rounded-2xl border active:opacity-80 ${
                featured ? 'bg-orange-500/5 border-orange-500/30' : 'bg-charcoal/50 border-white/10'
              }`}
            >
              {featured ? (
                <Text className="text-[11px] font-black uppercase tracking-wider text-orange-400 mb-2">
                  ★ Recommended Inspection
                </Text>
              ) : (
                <Text className="text-[11px] font-bold text-sky mb-2 uppercase">{display.label}</Text>
              )}
              <Text className={`font-black mb-1 ${featured ? 'text-lg text-white' : 'text-base text-white'}`}>{g.title}</Text>
              <Text className="text-xs text-white/40 mb-3" numberOfLines={3}>{g.description || ''}</Text>
              <View className="flex-row justify-between items-center">
                <Text className="text-xs text-white/30">{g.duration || ''}</Text>
                <Text className={`text-xs font-bold ${featured ? 'text-orange-400' : 'text-sky'}`}>View →</Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

// ─── Main screen ────────────────────────────────────────────────────────────

export default function GuideDetailScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { id } = route.params || {};
  const guide = useMemo(() => getGuideById(id), [id]);
  const { activeVehicle } = useGarage();

  const [view, setView] = useState('overview');         // 'overview' | 'steps' | 'complete'
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showStepList, setShowStepList] = useState(false);

  const suggestedGuides = useMemo(
    () => guide ? getSuggestedGuides(guide, allGuides, 4) : [],
    [guide],
  );

  // Set the navigation header title to the guide title.
  useEffect(() => {
    if (guide) navigation.setOptions({ title: guide.title });
  }, [guide, navigation]);

  // Hydrate progress from storage.
  useEffect(() => {
    if (!guide) return;
    let cancelled = false;
    storage.getItem(`garageiq_progress_${guide.id}`).then(saved => {
      if (cancelled) return;
      if (saved && Array.isArray(saved.completedSteps)) {
        setCompletedSteps(saved.completedSteps);
        if (saved.completedSteps.length === guide.steps.length) {
          setView('complete');
        } else if (saved.completedSteps.length > 0) {
          setCurrentStep(saved.currentStep || 0);
          setView('steps');
        }
      }
    });
    return () => { cancelled = true; };
  }, [guide]);

  if (!guide) {
    return (
      <View className="flex-1 bg-navy items-center justify-center px-6">
        <Text className="text-2xl font-black text-white mb-2">Guide not found</Text>
        <Text className="text-white/50 text-center mb-6">
          We couldn't find a guide with id "{String(id)}".
        </Text>
        <Pressable onPress={() => navigation.goBack()} className="bg-sky px-6 py-3 rounded-xl active:opacity-80">
          <Text className="text-white font-bold">Go Back</Text>
        </Pressable>
      </View>
    );
  }

  const step = guide.steps[currentStep];
  const progressPct = Math.round((completedSteps.length / guide.steps.length) * 100);

  function saveProgress(completed, stepIdx) {
    storage.setItem(`garageiq_progress_${guide.id}`, { completedSteps: completed, currentStep: stepIdx });
  }

  function markStepDone() {
    const next = Array.from(new Set([...completedSteps, currentStep]));
    setCompletedSteps(next);
    if (currentStep === guide.steps.length - 1) {
      saveProgress(next, currentStep);
      setView('complete');
    } else {
      saveProgress(next, currentStep + 1);
      setCurrentStep(currentStep + 1);
    }
  }

  function startOver() {
    storage.removeItem(`garageiq_progress_${guide.id}`);
    setCompletedSteps([]);
    setCurrentStep(0);
    setView('overview');
  }

  async function logMaintenance() {
    if (!activeVehicle) {
      Alert.alert('No active vehicle', 'Add a vehicle from the Garage tab first, then re-log this maintenance.');
      return;
    }
    Alert.prompt?.(
      'Log Maintenance',
      `Enter your current mileage to log "${guide.title}".`,
      async (value) => {
        const mileage = parseInt(value, 10);
        if (Number.isNaN(mileage)) {
          Alert.alert('Invalid mileage', 'Please enter a number.');
          return;
        }
        const logKey = `garageiq_maint_log_${activeVehicle.make}_${activeVehicle.model}_${activeVehicle.year}`;
        const existing = (await storage.getItem(logKey)) || [];
        const entry = {
          id: Date.now(),
          itemId: guide.id,
          itemName: guide.title,
          mileage,
          cost: 0,
          notes: `Completed: ${guide.title}`,
          date: new Date().toISOString().split('T')[0],
          timestamp: Date.now(),
        };
        await storage.setItem(logKey, [entry, ...existing]);
        Alert.alert('✓ Logged', `"${guide.title}" at ${mileage.toLocaleString()} miles.`);
      },
      'plain-text',
    );

    // iOS-only Alert.prompt — fall back on Android.
    if (!Alert.prompt) {
      Alert.alert('Log Maintenance', 'Mileage entry UI not available on Android Alert. Tap "Open Garage" and log there.');
    }
  }

  // ── COMPLETE VIEW ─────────────────────────────────────────────────────────
  if (view === 'complete') {
    return (
      <ScrollView className="flex-1 bg-navy" contentContainerStyle={{ padding: 24, paddingBottom: 64 }}>
        <View className="items-center mt-4 mb-6">
          <IconTrophy size={64} />
        </View>
        <Text className="text-4xl font-black text-white text-center mb-2">Job Complete!</Text>
        <Text className="text-base text-white/50 text-center mb-8">
          You finished{' '}
          <Text className="font-bold text-sky">{guide.title}</Text> on your{' '}
          {guide.vehicle.year} {guide.vehicle.make} {guide.vehicle.model}.
        </Text>

        <View className="flex-row gap-3 mb-8">
          {[
            { label: 'Steps', value: `${guide.steps.length}/${guide.steps.length}` },
            { label: 'Est. Savings', value: '$120+' },
            { label: 'Level', value: guide.difficulty },
          ].map(stat => (
            <View key={stat.label} className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-4 items-center">
              <Text className="text-lg font-black text-sky capitalize">{stat.value}</Text>
              <Text className="text-[11px] text-white/40 mt-1">{stat.label}</Text>
            </View>
          ))}
        </View>

        {!!guide.tips?.length && (
          <View className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
            <View className="flex-row items-center gap-2 mb-3">
              <IconSafety size={18} />
              <Text className="font-black text-white">Pro Tips</Text>
            </View>
            <View className="gap-2">
              {guide.tips.map((tip, i) => (
                <View key={i} className="flex-row gap-2">
                  <Text className="text-sky">→</Text>
                  <Text className="text-sm text-white/50 flex-1">{tip}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View className="gap-3">
          <Pressable onPress={logMaintenance} className="bg-green-600 py-3 rounded-xl items-center active:opacity-80">
            <Text className="text-white font-bold">📝 Log Maintenance</Text>
          </Pressable>
          <Pressable onPress={() => navigation.navigate('MainTabs', { screen: 'Guides' })} className="bg-sky py-3 rounded-xl items-center active:opacity-80">
            <Text className="text-white font-bold">Browse More Guides →</Text>
          </Pressable>
          <Pressable onPress={startOver} className="bg-white/5 border border-white/10 py-3 rounded-xl items-center active:opacity-80">
            <Text className="text-white font-bold">Start Over</Text>
          </Pressable>
        </View>

        <SuggestedGuides
          items={suggestedGuides}
          onPress={g => navigation.replace('GuideDetail', { id: g.id, title: g.title })}
        />
      </ScrollView>
    );
  }

  // ── STEPS VIEW ────────────────────────────────────────────────────────────
  if (view === 'steps') {
    return (
      <ScrollView className="flex-1 bg-navy" contentContainerStyle={{ padding: 16, paddingBottom: 48 }}>
        {/* Progress bar */}
        <View className="bg-charcoal/70 border border-white/10 rounded-2xl p-4 mb-5">
          <View className="flex-row items-center gap-3">
            <Pressable onPress={() => setView('overview')} className="active:opacity-60">
              <Text className="text-sm text-white/50 font-semibold">← Overview</Text>
            </Pressable>
            <View className="flex-1">
              <View className="flex-row justify-between items-center mb-1">
                <Text className="text-[11px] text-white/40 font-semibold" numberOfLines={1}>
                  Step {currentStep + 1} of {guide.steps.length}
                </Text>
                <Text className="text-sky font-black text-sm">{progressPct}%</Text>
              </View>
              <View className="h-2 bg-white/10 rounded-full overflow-hidden">
                <View className="h-2 bg-sky rounded-full" style={{ width: `${progressPct}%` }} />
              </View>
            </View>
            <Pressable onPress={() => setShowStepList(!showStepList)} className="active:opacity-60">
              <Text className="text-xs font-bold text-sky">{showStepList ? 'Hide' : 'All Steps'}</Text>
            </Pressable>
          </View>
          {showStepList && (
            <View className="mt-4 pt-4 border-t border-white/10 gap-1.5">
              {guide.steps.map((s, i) => {
                const done = completedSteps.includes(i);
                const active = i === currentStep;
                return (
                  <Pressable
                    key={i}
                    onPress={() => { setCurrentStep(i); setShowStepList(false); }}
                    className={`flex-row items-center gap-2 p-2 rounded-lg ${
                      active ? 'bg-sky/10' : ''
                    } active:opacity-80`}
                  >
                    <View className={`w-6 h-6 rounded-full items-center justify-center ${
                      done ? 'bg-green-500' : active ? 'bg-sky' : 'bg-white/10'
                    }`}>
                      <Text className="text-white text-[11px] font-black">{done ? '✓' : i + 1}</Text>
                    </View>
                    <Text
                      className={`text-sm flex-1 ${
                        active ? 'text-sky font-bold' : done ? 'text-green-400' : 'text-white/50'
                      }`}
                      numberOfLines={1}
                    >
                      {s.title}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>

        {/* Step body */}
        <View className="bg-charcoal/50 border border-white/10 rounded-2xl p-5">
          <View className="flex-row items-start gap-3 mb-5">
            <View className="w-12 h-12 rounded-xl bg-sky items-center justify-center">
              <Text className="text-white font-black text-xl">{currentStep + 1}</Text>
            </View>
            <View className="flex-1 pt-1">
              <Text className="text-xl font-black text-white">{step.title}</Text>
            </View>
          </View>

          <Text className="text-white/60 leading-6 text-base mb-2">{step.description}</Text>
          <SafetyCallout note={step.safetyNote} />
          <DiagramPlaceholder diagram={step.diagram} />

          {step.video?.startTime != null && guide.video?.youtubeId && (
            <YouTubeEmbed
              youtubeId={guide.video.youtubeId}
              title={guide.video.title}
              channel={guide.video.channel}
              startTime={step.video.startTime}
              endTime={step.video.endTime}
            />
          )}

          {/* Step user content (photos/notes) — deferred. */}
          <View className="mt-5 bg-white/5 border border-dashed border-white/10 rounded-xl p-4">
            <Text className="text-xs text-white/40 leading-5">
              📷 <Text className="font-bold">Photos & notes per step</Text> are available in the web app
              today; the mobile picker is in the v2 backlog.
            </Text>
          </View>

          {/* Nav buttons */}
          <View className="flex-row gap-3 mt-6 pt-5 border-t border-white/10">
            <Pressable
              onPress={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
              disabled={currentStep === 0}
              className={`flex-1 py-3 rounded-xl items-center border border-white/10 ${
                currentStep === 0 ? 'bg-white/5 opacity-40' : 'bg-white/5 active:opacity-80'
              }`}
            >
              <Text className="text-white font-bold">← Previous</Text>
            </Pressable>
            <Pressable
              onPress={markStepDone}
              className="flex-1 bg-sky py-3 rounded-xl items-center active:opacity-80"
            >
              <Text className="text-white font-bold">
                {currentStep === guide.steps.length - 1 ? 'Finish Guide' : 'Mark Done →'}
              </Text>
            </Pressable>
          </View>

          {/* Progress dots */}
          <View className="mt-6 pt-5 border-t border-white/10">
            <View className="flex-row items-center justify-between mb-3">
              <Text className="font-black text-white text-sm">Your Progress</Text>
              <Text className="text-xs text-white/30">{completedSteps.length} of {guide.steps.length} complete</Text>
            </View>
            <View className="flex-row flex-wrap" style={{ gap: 6 }}>
              {guide.steps.map((_, i) => {
                const done = completedSteps.includes(i);
                const active = i === currentStep;
                return (
                  <Pressable
                    key={i}
                    onPress={() => setCurrentStep(i)}
                    className={`w-9 h-9 rounded-lg items-center justify-center ${
                      done ? 'bg-green-500' : active ? 'bg-sky' : 'bg-white/10'
                    } active:opacity-80`}
                  >
                    <Text className={`text-xs font-black ${done || active ? 'text-white' : 'text-white/30'}`}>
                      {done ? '✓' : i + 1}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>

        <PartsPanel parts={guide.parts || []} />
      </ScrollView>
    );
  }

  // ── OVERVIEW VIEW ─────────────────────────────────────────────────────────
  return (
    <ScrollView className="flex-1 bg-navy" contentContainerStyle={{ padding: 20, paddingBottom: 48 }}>
      <Text className="text-xs text-white/30 mb-4">
        <Text>Guides › </Text>
        <Text className="text-white/60 font-semibold">{guide.title}</Text>
      </Text>

      <View className="flex-row flex-wrap items-center gap-2 mb-3">
        <DifficultyBadge level={guide.difficulty} />
        <Text className="text-sm text-white/30">{guide.duration}</Text>
        <Text className="text-sm text-white/30">· {guide.steps.length} steps</Text>
      </View>

      <Text className="text-3xl font-black text-white mb-1">{guide.title}</Text>
      <Text className="text-base text-white/40 mb-2">
        {guide.vehicle.year} {guide.vehicle.make} {guide.vehicle.model} {guide.vehicle.trim}
      </Text>
      {!!guide.interval && (
        <Text className="text-xs text-white/30 mb-4">
          Maintenance interval: <Text className="font-semibold text-white/50">{guide.interval}</Text>
        </Text>
      )}

      <Text className="text-white/60 leading-6 text-base mb-5">{guide.description}</Text>

      {/* Full guide video */}
      {guide.video?.youtubeId && (
        <View className="mb-2">
          <View className="flex-row items-center gap-2 mb-2">
            <IconVideo size={18} />
            <Text className="font-black text-white">Full Repair Video</Text>
          </View>
          <YouTubeEmbed
            youtubeId={guide.video.youtubeId}
            title={guide.video.title}
            channel={guide.video.channel}
            height={220}
          />
        </View>
      )}

      {/* Tools */}
      {!!guide.tools?.length && (
        <View className="bg-charcoal/40 border border-white/10 rounded-2xl p-5 mt-6">
          <View className="flex-row items-center gap-2 mb-3">
            <IconWrench size={18} />
            <Text className="font-black text-white">Tools You'll Need</Text>
          </View>
          <View className="gap-2">
            {guide.tools.map((tool, i) => {
              const name  = typeof tool === 'string' ? tool : tool.name;
              const notes = typeof tool === 'object' ? tool.notes : null;
              return (
                <View key={i} className="flex-row items-start gap-2">
                  <View className="mt-1"><IconCheck size={14} /></View>
                  <Text className="text-sm flex-1">
                    <Text className="font-medium text-white/70">{name}</Text>
                    {notes && <Text className="text-white/30"> — {notes}</Text>}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>
      )}

      {/* Steps overview */}
      <View className="bg-charcoal/40 border border-white/10 rounded-2xl p-5 mt-4">
        <View className="flex-row items-center gap-2 mb-3">
          <IconSteps size={18} />
          <Text className="font-black text-white">Steps Overview</Text>
        </View>
        <View className="gap-3">
          {guide.steps.map((s, i) => (
            <View key={i} className="flex-row items-start gap-3">
              <View className="w-6 h-6 rounded-full bg-white/10 items-center justify-center mt-0.5">
                <Text className="text-[11px] font-black text-white/40">{i + 1}</Text>
              </View>
              <Text className="text-sm text-white/50 flex-1">{s.title}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Manual source / disclaimer */}
      <View className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4">
        <View className="flex-row items-center gap-1 mb-1">
          <IconBook size={16} />
          <Text className="font-bold text-white/70">Source</Text>
        </View>
        <Text className="text-sm text-white/50">
          {guide.manualSource?.notes ||
            "Created through independent research using the Automotive Repair Guide Generation System v9. Not OEM documentation — always verify with your vehicle manual."}
        </Text>
      </View>

      <Pressable
        onPress={() => { setView('steps'); setCurrentStep(0); }}
        className="bg-sky py-4 rounded-2xl items-center mt-6 active:opacity-80"
      >
        <Text className="text-white font-black text-lg">Start Guide →</Text>
      </Pressable>

      <PartsPanel parts={guide.parts || []} />

      <SuggestedGuides
        items={suggestedGuides}
        onPress={g => navigation.replace('GuideDetail', { id: g.id, title: g.title })}
      />
    </ScrollView>
  );
}
